import { EngineAudio } from '../audio/engine';
import { CouchHost, CouchMode } from './couch';
import { OnlineMode } from './online';
import { ApiError, GhostStartData, LadderChallenge, RunSummary, api, dropQueuedDaily, flushQueue, loadProfile, queueDaily, queueLap } from '../net/api';
import type { Streak } from '../shared/streak';
import { Site, isSitePath, share, shareText } from '../site/site';
import { DAILY_MAX_TICKS, DailySpec, dailyMods, dailyTeam, decodeInputs, encodeInputs, trackById } from '../sim/daily';
import type { Mods } from '../sim/mods';
import { InputManager } from '../input/input';
import { CarView, Renderer, lerpCar } from '../render/renderer';
import { trackMapSvg } from '../render/trackArt';
import { aiInput } from '../sim/ai';
import { autopilot } from '../sim/autopilot';
import { CarState, Input, carPerf, quantise, speedOf } from '../sim/car';
import { DRIVERS, DriverSpec } from '../sim/drivers';
import { RaceEvent, RaceWorld, RacerSpec, ResultRow, gridFromQualifying, qualifyAi } from '../sim/race';
import {
  AI_PACE, Challenge, CrashRuleName, Difficulty, PLAYER_ID, SeasonState, aiChallenges, applyRound, driverName,
  engagedIn, newSeason, raceSpecs, seasonOver, seasonSpecs, standings, teamSheet,
} from '../sim/season';
import { DEFAULT_TEAM, TEAMS, TeamSpec, teamById } from '../sim/teams';
import { Track } from '../sim/track';
import { CALENDAR } from '../sim/tracks/index';
import { DT, STREAK_LAP_FRACTION, mphFromPxs } from '../sim/tuning';
import { World } from '../sim/world';
import { fmtDelta, fmtTime } from './format';
import {
  GhostData, clearSeason, loadBest, loadGhost, loadSeasonRaw, loadSetting, loadStats, saveBest, saveGhost,
  saveSeasonRaw, saveSetting, saveStats,
} from './storage';

type Kind = 'trial' | 'qual' | 'race' | 'couch' | 'daily' | 'online';
type Mode = 'title' | 'sheet' | 'lights' | 'playing' | 'paused' | 'cooldown' | 'site';

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;

function snap(c: CarState): CarState {
  return { ...c };
}

interface Recording {
  start: GhostData['start'];
  inputs: Input[];
  splits: number[];
}

interface Weekend {
  kind: 'quick' | 'season';
  track: Track;
  seed: number;
  specs: RacerSpec[];
  /** Index of the human in `specs`. */
  me: number;
  qual: (number | null)[];
  grid: number[];
  results: ResultRow[] | null;
  /** Season only: this round's challenges (driver ids). */
  challenges: Challenge[];
}

/** The human's car, driven by the AI for debug runs. */
const AUTO_DRIVER: DriverSpec = { ...DRIVERS[0], id: 'auto', shortcut: 0, mistakeRate: 0, consistency: 1 };

export class Game {
  private renderer = new Renderer();
  private input = new InputManager();
  private audio = new EngineAudio();
  private trackIndex = Math.max(0, CALENDAR.findIndex((d) => d.id === loadSetting('track', CALENDAR[0].id)));
  private track = new Track(CALENDAR[this.trackIndex]);
  private shownTrackId = '';
  private teamIndex = TEAMS.findIndex((t) => t.id === loadSetting('team', DEFAULT_TEAM));
  private difficulty: Difficulty = (['rookie', 'pro', 'legend'] as const).find((d) => d === loadSetting('difficulty', 'rookie')) ?? 'rookie';
  private crashRule: CrashRuleName = loadSetting('crashRule', 'respawn') === 'classic' ? 'classic' : 'respawn';
  private kind: Kind = 'trial';
  private mode: Mode = 'title';
  private acc = 0;
  private lastTs = 0;
  private lastControls = { steer: 0, throttle: 0, brake: 0 };
  private bannerTimer = 0;
  private hudCache = new Map<string, string>();
  private lightsStart = 0;
  private lastBeep = -1;
  private flashes: { x: number; y: number; f: number }[] = [];
  private cooldown = 0;
  private afterCooldown: (() => void) | null = null;
  private autoDrive = false;
  /** Keyboard/gamepad navigation inside a sheet (paddock rows). */
  private sheetNav: { up: () => void; down: () => void } | null = null;

  // Time trial
  private world!: World;
  private prev!: CarState;
  private rec: Recording | null = null;
  private ghost: GhostData | null = null;
  private ghostWorld: World | null = null;
  private ghostPrev: CarState | null = null;
  private ghostTick = 0;

  // Race weekend (quick race or a season round)
  private weekend: Weekend | null = null;
  private race: RaceWorld | null = null;
  private prevCars: CarState[] = [];
  private meIdx = 0;
  private lastPos = 0;
  private season: SeasonState | null = this.loadSeason();

  // Head-to-Head (src/game/couch.ts) and online races (src/game/online.ts)
  private couch!: CouchMode;
  private online!: OnlineMode;

  // The website (src/site) and the Daily Grand Prix
  private site!: Site;
  /** Set while practising or racing a Daily (practice: kind 'trial'; ranked: kind 'daily'). */
  private dailyCtx: { spec: DailySpec; ranked: boolean; inputs: Input[]; done: boolean } | null = null;
  /**
   * A Time Trial with a purpose (design.md B14.4, B14.7): a Seat Ladder
   * challenge (your laps go to the challenge) or a "beat my lap" ghost link
   * (someone else's record lap as your ghost).
   */
  private trialCtx: {
    kind: 'ladder' | 'ghost';
    trackId: string;
    teamId: string;
    ghost: GhostData | null;
    ghostName: string;
    challenge?: LadderChallenge;
  } | null = null;
  /** Attract mode: an AI race behind the title screen (design.md B14.2). */
  private attract: RaceWorld | null = null;
  private attractPrev: CarState[] = [];
  private attractAcc = 0;
  private attractCam: [number, number] | null = null;

  async start(): Promise<void> {
    if (this.teamIndex < 0) this.teamIndex = 4;
    await this.renderer.init($('stage'));
    this.couch = new CouchMode(this.couchHost());
    this.online = new OnlineMode({
      renderer: this.renderer,
      audio: this.audio,
      input: this.input,
      sheet: (k, t, sub, body, actions) => this.sheet(k, t, sub, body, actions),
      banner: (text, sub, cls, secs) => this.banner(text, sub, cls, secs),
      hideAll: () => this.hideAll(),
      home: () => this.showTitle(),
      setUrl: (path) => {
        if (location.pathname !== path) history.replaceState(null, '', path);
      },
      enterRace: (touch) => {
        this.hideAll();
        this.kind = 'online';
        this.race = null;
        this.weekend = null;
        this.attract = null;
        this.mode = 'playing';
        document.body.classList.add('racing');
        $('topbar').hidden = false;
        $('btn-restart').hidden = true;
        $('touch').hidden = !touch;
        this.renderer.anchorY = touch ? 0.56 : 0.46;
        this.hudCache.clear();
      },
      defaultTrack: () => CALENDAR[this.trackIndex].id,
      showTrack: (id) => this.useTrack(trackById(id)),
    });
    this.site = new Site({
      go: (path) => this.go(path),
      startDaily: (spec, ranked) => this.startDaily(spec, ranked),
      startChallenge: (c) => this.startChallenge(c),
      blip: (f, d) => this.audio.blip(f, d),
      localBest: (trackId) => {
        let best: { ticks: number; team: string } | null = null;
        for (const t of TEAMS) {
          const b = loadBest(trackId, t.id);
          if (b !== null && (!best || b < best.ticks)) best = { ticks: b, team: t.id };
        }
        return best;
      },
    });
    window.addEventListener('popstate', () => this.route(location.pathname));
    void flushQueue();
    this.useTrack(this.track);
    this.newWorld();
    this.detectTouch();
    this.bindUi();
    this.route(location.pathname);
    requestAnimationFrame((ts) => this.frame(ts));
  }

  /** What Head-to-Head borrows from the Game. */
  private couchHost(): CouchHost {
    return {
      renderer: this.renderer,
      audio: this.audio,
      input: this.input,
      sheet: (k, t, sub, body, actions) => this.sheet(k, t, sub, body, actions),
      banner: (text, sub, cls, secs) => this.banner(text, sub, cls, secs),
      hideAll: () => this.hideAll(),
      showTitle: () => this.showTitle(),
      play: (touch) => {
        this.hideAll();
        this.kind = 'couch';
        this.race = null;
        this.weekend = null;
        this.mode = 'playing';
        this.acc = 0;
        document.body.classList.add('racing');
        $('couch-hud').hidden = false;
        $('topbar').hidden = false;
        $('btn-restart').hidden = true;
        $('touch').hidden = !touch;
      },
      finish: (secs, then) => {
        this.mode = 'cooldown';
        this.cooldown = secs;
        this.afterCooldown = then;
      },
      track: () => this.track,
      cycleTrack: (d) => this.cycleTrack(d),
      online: () => this.online.create(),
      aiPace: () => AI_PACE[this.difficulty],
    };
  }

  private get team(): TeamSpec {
    if (this.dailyCtx) return dailyTeam(this.dailyCtx.spec);
    if (this.trialCtx) return teamById(this.trialCtx.teamId);
    return TEAMS[this.teamIndex];
  }

  /** Storage key for bests/ghosts: the Daily has its own (its car is modified). */
  private get trialKey(): string {
    if (this.dailyCtx) return `daily-${this.dailyCtx.spec.date}`;
    const c = this.trialCtx?.challenge;
    if (c) return `ladder-${c.id}-${c.mine}`;
    return this.track.def.id;
  }

  private get mods(): Mods | null {
    return this.dailyCtx ? dailyMods(this.dailyCtx.spec) : null;
  }

  // ================================================================ routing

  /** Navigate within the app (real URLs: /daily, /records, /driver/abc...). */
  private go(path: string): void {
    if (location.pathname !== path) history.pushState(null, '', path);
    this.route(path);
  }

  private route(path: string): void {
    const race = /^\/race\/([A-Za-z2-9]{5})\/?$/.exec(path);
    if (race) {
      // An invite link: straight into that online room.
      this.showTitle(false);
      this.online.join(race[1].toUpperCase());
      return;
    }
    const ghost = /^\/ghost\/([a-z0-9]{10})\/([a-z-]+)\/([a-z]+)\/?$/.exec(path);
    if (ghost) {
      void this.startGhostLink(ghost[1], ghost[2], ghost[3]);
      return;
    }
    if (isSitePath(path)) this.showSite(path);
    else this.showTitle(false);
  }

  private showSite(path: string): void {
    if (this.online?.active) this.online.disconnect();
    this.leaveDaily();
    this.hideAll();
    this.mode = 'site';
    this.race = null;
    this.weekend = null;
    this.ensureAttract();
    document.body.classList.add('site-open');
    this.site.show(path);
  }

  private leaveDaily(): void {
    if (!this.dailyCtx && !this.trialCtx) return;
    this.dailyCtx = null;
    this.trialCtx = null;
    this.renderer.setConditions('dry');
    this.useTrack(new Track(CALENDAR[this.trackIndex]));
    this.newWorld();
  }

  /** Switch the active circuit (rebuilds the art only when it changes). */
  private useTrack(track: Track): void {
    this.track = track;
    if (this.shownTrackId !== track.def.id) {
      this.renderer.setTrack(track);
      this.shownTrackId = track.def.id;
    }
  }

  private loadSeason(): SeasonState | null {
    const raw = loadSeasonRaw() as SeasonState | null;
    if (!raw || raw.v !== 1 || typeof raw.round !== 'number' || !raw.seats) return null;
    return raw;
  }

  // =============================================================== UI wiring

  private detectTouch(): void {
    const coarse = window.matchMedia('(pointer: coarse)').matches || 'ontouchstart' in window;
    document.body.classList.toggle('touch', coarse);
    this.input.touchActive = coarse;
    this.input.autoAccelerate = loadSetting('autogas', coarse ? '1' : '0') === '1';
    document.body.classList.toggle('autogas', this.input.autoAccelerate);
    window.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'touch' && !document.body.classList.contains('touch')) {
        document.body.classList.add('touch');
        this.input.touchActive = true;
        this.refreshOptions();
      }
    });
  }

  private bindUi(): void {
    for (const el of document.querySelectorAll<HTMLElement>('#touch .tbtn')) {
      this.input.bindTouch(el, el.dataset.k as 'left' | 'right' | 'gas' | 'brake');
    }
    const unlock = () => this.audio.unlock();
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);

    $('t-prev').onclick = () => this.cycleTeam(-1);
    $('t-next').onclick = () => this.cycleTeam(1);
    $('trk-prev').onclick = () => this.cycleTrack(-1);
    $('trk-next').onclick = () => this.cycleTrack(1);
    $('btn-season').onclick = () => this.seasonButton();
    $('btn-newseason').onclick = () => this.confirmNewSeason();
    $('btn-weekend').onclick = () => this.startQuickRace();
    $('btn-trial').onclick = () => this.startTrial();
    $('btn-couch').onclick = () => {
      this.audio.unlock();
      this.couch.showLobby();
    };
    $('opt-sound').onclick = () => {
      this.audio.setMuted(!this.audio.muted);
      this.refreshOptions();
    };
    $('opt-auto').onclick = () => {
      this.input.autoAccelerate = !this.input.autoAccelerate;
      saveSetting('autogas', this.input.autoAccelerate ? '1' : '0');
      document.body.classList.toggle('autogas', this.input.autoAccelerate);
      this.refreshOptions();
    };
    $('opt-diff').onclick = () => {
      const order: Difficulty[] = ['rookie', 'pro', 'legend'];
      this.difficulty = order[(order.indexOf(this.difficulty) + 1) % order.length];
      saveSetting('difficulty', this.difficulty);
      this.refreshOptions();
    };
    $('opt-crash').onclick = () => {
      this.crashRule = this.crashRule === 'respawn' ? 'classic' : 'respawn';
      saveSetting('crashRule', this.crashRule);
      this.refreshOptions();
    };
    $('opt-full').onclick = () => this.toggleFullscreen();
    $('btn-pause').onclick = () => this.pause();
    $('btn-restart').onclick = () => this.restartTrial();
    $('p-resume').onclick = () => this.resume();
    $('p-restart').onclick = () => this.restartTrial();
    $('p-retire').onclick = () => this.retire();
    $('p-menu').onclick = () => {
      if (this.kind === 'online') this.online.leaveRace();
      else if (this.kind === 'daily' && this.dailyCtx && !this.dailyCtx.done) this.retire(); // leaving a ranked run = DNF
      else if (this.dailyCtx) this.go('/daily');
      else if (this.trialCtx?.kind === 'ladder') this.go('/ladder');
      else this.showTitle();
    };

    this.input.onAction((a) => {
      if (this.mode === 'title') {
        if (a === 'left') this.cycleTeam(-1);
        if (a === 'right') this.cycleTeam(1);
        if (a === 'up') this.cycleTrack(-1);
        if (a === 'down') this.cycleTrack(1);
        if (a === 'start') this.seasonButton();
      } else if (this.mode === 'sheet') {
        if (a === 'up') this.sheetNav?.up();
        if (a === 'down') this.sheetNav?.down();
        if (a === 'start') ($('sheet-actions').querySelector('button.primary') as HTMLButtonElement | null)?.click();
        if (a === 'pause' && this.couch.open) this.couch.startFromPad();
      } else if (this.mode === 'playing' || this.mode === 'lights') {
        if (a === 'pause') this.pause();
        if (a === 'restart' && this.kind === 'trial') this.restartTrial();
      } else if (this.mode === 'paused') {
        if (a === 'pause' || a === 'start') this.resume();
        if (a === 'restart' && this.kind === 'trial') this.restartTrial();
      }
      if (a === 'fullscreen') this.toggleFullscreen();
      if (a === 'mute') {
        this.audio.setMuted(!this.audio.muted);
        this.refreshOptions();
      }
      if (a === 'camera') this.renderer.classicCamera = !this.renderer.classicCamera;
    });

    // Auto-pause when the tab is hidden or the window loses focus (design.md B10).
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && (this.mode === 'playing' || this.mode === 'lights') && this.kind !== 'online') this.pause();
    });
    window.addEventListener('blur', () => {
      if ((this.mode === 'playing' || this.mode === 'lights') && !this.autoDrive && this.kind !== 'online') this.pause();
    });
    window.addEventListener('gamepadconnected', () => document.body.classList.add('pad'));
  }

  private refreshOptions(): void {
    $('opt-sound').textContent = `SOUND: ${this.audio.muted ? 'OFF' : 'ON'}`;
    $('opt-auto').textContent = `AUTO GAS: ${this.input.autoAccelerate ? 'ON' : 'OFF'}`;
    $('opt-full').textContent = document.fullscreenElement ? 'EXIT FULLSCREEN' : 'FULLSCREEN';
    $('opt-diff').textContent = `AI: ${this.difficulty.toUpperCase()}`;
    $('opt-crash').textContent = `CRASHES: ${this.crashRule === 'classic' ? 'CLASSIC' : 'RESPAWN'}`;
    $('opt-crash').title =
      this.crashRule === 'classic' ? 'The 1992 rule: one crash and your race is over' : 'Crash and you rejoin after a 3-second penalty';
  }

  private toggleFullscreen(): void {
    if (document.fullscreenElement) {
      void document.exitFullscreen?.();
    } else {
      const el = document.documentElement as HTMLElement & { webkitRequestFullscreen?: () => void };
      const p = el.requestFullscreen ? el.requestFullscreen({ navigationUI: 'hide' }) : (el.webkitRequestFullscreen?.(), undefined);
      Promise.resolve(p)
        .then(() => (screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> })?.lock?.('landscape'))
        .catch(() => undefined);
    }
    setTimeout(() => this.refreshOptions(), 300);
  }

  private cycleTeam(d: number): void {
    this.teamIndex = (this.teamIndex + d + TEAMS.length) % TEAMS.length;
    saveSetting('team', this.team.id);
    this.newWorld();
    this.renderTitle();
    this.audio.blip(660, 0.05);
  }

  private cycleTrack(d: number): void {
    this.trackIndex = (this.trackIndex + d + CALENDAR.length) % CALENDAR.length;
    saveSetting('track', CALENDAR[this.trackIndex].id);
    this.useTrack(new Track(CALENDAR[this.trackIndex]));
    this.newWorld();
    this.renderTitle();
    this.audio.blip(520, 0.05);
  }

  private renderTitle(): void {
    const t = this.team;
    $('t-team').textContent = t.name;
    $('t-tier').textContent = `TIER ${t.tier}`;
    $('t-swatch').style.background = `linear-gradient(90deg, #${hex(t.primary)} 65%, #${hex(t.secondary)} 65%)`;
    $('t-acc-v').textContent = `${t.zeroTo100.toFixed(1)}s`;
    $('t-top-v').textContent = `${t.topSpeedMph}`;
    $('t-acc').style.width = `${30 + ((8.4 - t.zeroTo100) / (8.4 - 6.3)) * 70}%`;
    $('t-top').style.width = `${30 + ((t.topSpeedMph - 180) / (220 - 180)) * 70}%`;
    $('t-best').textContent = fmtTime(loadBest(this.track.def.id, t.id));
    const def = this.track.def;
    $('t-track').textContent = def.name;
    $('t-sub').textContent = `${def.country} · ${def.subtitle}`;
    $('t-mode').textContent = `${def.raceLaps}-LAP RACE`;
    $('t-rec').textContent = fmtTime(Math.round(def.defaultRecord * 120));
    $('t-map').innerHTML = trackMapSvg(this.track, 56);

    // Season card.
    const s = this.season;
    if (s && !seasonOver(s)) {
      const next = new Track(CALENDAR[s.round]);
      const table = standings(s);
      const myPos = table.findIndex((r) => r.driver === PLAYER_ID) + 1;
      $('s-map').innerHTML = trackMapSvg(next, 96);
      $('s-line').innerHTML = `Next: <b>ROUND ${s.round + 1}/6 · ${next.def.country}</b><br>You: P${myPos} in the championship, ${s.points[PLAYER_ID]} pts, driving the ${teamById(s.seats[PLAYER_ID]).name}.`;
      $('s-sub').textContent = `AI ${s.difficulty.toUpperCase()} · crashes ${s.crashRule.toUpperCase()}`;
      $('btn-season').textContent = 'CONTINUE';
      $('btn-newseason').hidden = false;
    } else {
      $('s-map').innerHTML = trackMapSvg(new Track(CALENDAR[0]), 96);
      $('s-line').textContent = 'Six rounds. Start in the Bennet, like 1992. Race for points, and challenge rivals to steal their cars.';
      $('s-sub').textContent = s && seasonOver(s) ? 'Last season complete. Ready for another?' : '';
      $('btn-season').textContent = 'NEW SEASON';
      $('btn-newseason').hidden = true;
    }
    void this.site.renderTitleCard();
    const st = loadStats();
    $('t-stats').innerHTML = st.races
      ? `RACES <b>${st.races}</b> &middot; WINS <b>${st.wins}</b> &middot; PODIUMS <b>${st.podiums}</b> &middot; POINTS <b>${st.points}</b>`
      : '';
  }

  // ============================================================ screen chrome

  private hideAll(): void {
    for (const id of ['title', 'pause', 'sheet', 'hud', 'topbar', 'touch', 'lights', 'tower', 'couch-hud']) $(id).hidden = true;
    this.site?.hide();
    document.body.classList.remove('site-open', 'racing');
    $('banner').classList.remove('show');
    this.bannerTimer = 0;
  }

  private showHud(): void {
    $('hud').hidden = false;
    $('topbar').hidden = false;
    $('btn-restart').hidden = this.kind !== 'trial';
    const touch = document.body.classList.contains('touch');
    $('touch').hidden = !touch;
    this.renderer.anchorY = touch ? 0.56 : 0.46;
    $('tower').hidden = this.kind !== 'race';
    $('hud-pos').hidden = this.kind !== 'race';
    $('hud-rival').hidden = true;
    this.hudCache.clear();
  }

  private setHudTeam(team: TeamSpec): void {
    const chip = $('hud-team');
    chip.textContent = team.name;
    chip.style.background = `#${hex(team.primary)}`;
    chip.style.color = luminance(team.primary) > 0.5 ? '#111' : '#fff';
  }

  private sheet(
    kicker: string,
    title: string,
    sub: string,
    body: HTMLElement | string,
    actions: { label: string; primary?: boolean; onClick: () => void }[],
  ): void {
    this.hideAll();
    this.mode = 'sheet';
    this.sheetNav = null;
    $('sheet').hidden = false;
    $('sheet-kicker').textContent = kicker;
    $('sheet-title').textContent = title;
    $('sheet-sub').textContent = sub;
    const b = $('sheet-body');
    b.innerHTML = '';
    if (typeof body === 'string') b.innerHTML = body;
    else b.appendChild(body);
    const a = $('sheet-actions');
    a.innerHTML = '';
    for (const act of actions) {
      const btn = document.createElement('button');
      btn.textContent = act.label;
      btn.className = act.primary ? 'primary' : 'secondary';
      btn.onclick = act.onClick;
      a.appendChild(btn);
    }
    $('sheet').scrollTop = 0;
  }

  // ==================================================================== title

  private showTitle(push = true): void {
    if (push && location.pathname !== '/') history.pushState(null, '', '/');
    if (this.online?.active) this.online.disconnect();
    this.dailyCtx = null;
    this.trialCtx = null;
    this.renderer.setConditions('dry');
    this.hideAll();
    this.mode = 'title';
    if (this.couch) this.couch.match = null;
    this.race = null;
    this.weekend = null;
    this.useTrack(new Track(CALENDAR[this.trackIndex]));
    $('title').hidden = false;
    this.newWorld();
    this.attract = null;
    this.ensureAttract();
    this.renderTitle();
    this.refreshOptions();
  }

  // ============================================================ attract mode

  /** Six AI cars racing on the selected track behind the menus. */
  private ensureAttract(): void {
    if (this.attract && this.attract.track === this.track) {
      this.renderer.setRivals(this.attract.racers.map((r) => ({ team: r.spec.team, tag: r.spec.tag })));
      return;
    }
    const specs: RacerSpec[] = DRIVERS.map((d) => ({ id: d.id, name: d.name, tag: d.tag, team: teamById(d.teamId), driver: d }));
    specs.push({ id: 'bennet', name: 'BEN', tag: 'BEN', team: teamById('bennet'), driver: { ...DRIVERS[2], id: 'ben', tag: 'BEN' } });
    const grid = specs.map((_, i) => i).reverse();
    this.attract = new RaceWorld(this.track, specs, grid, { laps: 1e9, crashRule: 'respawn', aiPace: 1.25, seed: (Math.random() * 1e9) >>> 0 });
    this.attractPrev = this.attract.racers.map((r) => snap(r.car));
    this.attractAcc = 0;
    this.attractCam = null;
    this.renderer.setRivals(specs.map((sp) => ({ team: sp.team, tag: sp.tag })));
  }

  private renderAttract(dtFrame: number): void {
    const w = this.attract!;
    this.attractAcc += dtFrame;
    let n = 0;
    while (this.attractAcc >= DT && n < 8) {
      this.attractPrev = w.racers.map((r) => snap(r.car));
      w.step();
      this.attractAcc -= DT;
      n++;
    }
    if (n === 8) this.attractAcc = 0;
    const alpha = this.attractAcc / DT;
    const cars = w.racers.map((r, i) => ({ ...lerpCar(this.attractPrev[i] ?? r.car, r.car, alpha), alpha: r.ghostTicks > 0 ? 0.5 : 1 }));
    const lead = cars[w.standings()[0]];
    if (!this.attractCam) this.attractCam = [lead.x, lead.y];
    const k = 1 - Math.exp(-dtFrame * 2);
    this.attractCam[0] += (lead.x - this.attractCam[0]) * k;
    this.attractCam[1] += (lead.y - this.attractCam[1]) * k;
    this.renderer.drawShared(this.attractCam[0], this.attractCam[1], 520, 300, 1, cars, [], false, false);
    this.audio.update(0, 0, false);
  }

  // =============================================================== time trial

  private newWorld(): void {
    this.world = new World(this.track, this.team, 0, this.mods);
    this.world.lap.bestLapTicks = loadBest(this.trialKey, this.team.id);
    this.prev = snap(this.world.car);
    this.renderer.setTeam(this.team);
    this.renderer.clearSkids();
    this.ghost = this.trialCtx?.ghost ?? loadGhost(this.trialKey, this.team.id);
    this.ghostWorld = null;
    this.renderer.setGhost(this.ghost ? teamById(this.ghost.team) : null);
    this.rec = null;
  }

  private startTrial(): void {
    this.audio.unlock();
    this.hideAll();
    this.kind = 'trial';
    this.race = null;
    this.renderer.setRivals([]);
    this.newWorld();
    this.setHudTeam(this.team);
    this.showHud();
    this.startLights();
  }

  // =============================================== ladder + ghost-link trials

  private startTrialCtx(ctx: NonNullable<Game['trialCtx']>, title: string, sub: string): void {
    this.audio.unlock();
    this.hideAll();
    if (this.couch) this.couch.match = null;
    this.dailyCtx = null;
    this.trialCtx = ctx;
    this.kind = 'trial';
    this.race = null;
    this.weekend = null;
    this.attract = null;
    this.renderer.setRivals([]);
    this.renderer.setConditions('dry');
    this.useTrack(trackById(ctx.trackId));
    this.newWorld();
    this.setHudTeam(this.team);
    this.showHud();
    this.banner(title, sub, '', 3);
    this.startLights();
  }

  /** Someone else's lap as a ghost (their start state + inputs, replayed for its splits). */
  private remoteGhost(trackId: string, teamId: string, start: GhostStartData, inputs: string, ticks: number): GhostData | null {
    const list = decodeInputs(inputs, 120 * 120);
    if (!list) return null;
    const team = teamById(teamId);
    const w = new World(trackById(trackId), team);
    Object.assign(w.car, { x: start.x, y: start.y, vx: start.vx, vy: start.vy, h: start.h, bonusMph: start.bonusMph });
    w.perf = carPerf(team, start.bonusMph);
    w.lap = { ...w.lap, startTick: 0, nextGate: 1, lastGate: 0, streakDist: start.streakDist };
    const splits: number[] = [];
    for (const inp of list) {
      w.step(inp);
      for (const e of w.events) if (e.type === 'checkpoint') splits[e.gate] = w.tick - w.lap.startTick;
    }
    return { v: 1, track: `ghost-${trackId}`, team: teamId, ticks, start, splits, inputs: list };
  }

  /** /ghost/<driver>/<track>/<team>: "beat my lap". */
  private async startGhostLink(driver: string, trackId: string, teamId: string): Promise<void> {
    this.showTitle(false);
    try {
      const g = await api.ghost(driver, trackId, teamId);
      const ghost = this.remoteGhost(g.track, g.team, g.start, g.inputs, g.ticks);
      if (!ghost) throw new Error('bad ghost');
      history.replaceState(null, '', `/ghost/${driver}/${trackId}/${teamId}`);
      this.startTrialCtx(
        { kind: 'ghost', trackId: g.track, teamId: g.team, ghost, ghostName: g.initials },
        `BEAT ${g.initials}: ${fmtTime(g.ticks)}`,
        `${trackById(g.track).def.name} in the ${teamById(g.team).name}. The ghost is their record lap.`,
      );
    } catch {
      this.sheet('GHOST LINK', 'LAP NOT FOUND', "That lap isn't on the records board any more.", '', [{ label: 'HOME', primary: true, onClick: () => this.showTitle() }]);
    }
  }

  /** Drive for a Seat Ladder challenge (from the /ladder page). */
  private startChallenge(c: LadderChallenge): void {
    const mine = c.mine;
    if (!mine) return;
    const team = mine === 'challenger' ? c.challenger.team : c.defender!.team;
    const ghost = mine === 'defender' && c.ghost ? this.remoteGhost(c.track, c.challenger.team, c.ghost.start, c.ghost.inputs, c.ghost.ticks) : null;
    const name = trackById(c.track).def.name;
    let title: string;
    let sub: string;
    if (mine === 'defender') {
      title = `DEFEND: BEAT ${fmtTime(c.target)}`;
      sub = `${c.challenger.initials} wants your ${teamById(team).name}. The ghost is their lap.`;
    } else if (c.emptySeat) {
      title = `EMPTY SEAT: BEAT ${fmtTime(c.target)}`;
      sub = `${name} in your ${teamById(team).name}. Do it and the ${teamById(c.defender?.team ?? '').name || 'seat'} is yours.`;
    } else {
      title = `SET YOUR CHALLENGE LAP`;
      sub = `${name} in your ${teamById(team).name}. Then send it from the Ladder: ${c.defender?.initials} gets 48 h to beat it.`;
    }
    this.startTrialCtx({ kind: 'ladder', trackId: c.track, teamId: team, ghost, ghostName: c.challenger.initials ?? '', challenge: c }, title, sub);
  }

  /** A personal best during a ladder challenge goes to the server (replayed there). */
  private submitChallengeLap(g: GhostData): void {
    const c = this.trialCtx?.challenge;
    if (!c) return;
    api
      .challengeLap(c.id, { ticks: g.ticks, start: g.start, inputs: encodeInputs(g.inputs) })
      .then((r) => {
        if (this.trialCtx?.challenge?.id !== r.id) return;
        this.trialCtx.challenge = r;
        if (r.state === 'won') this.ladderResult('SEAT TAKEN!', `You're in the ${teamById(tierTeamName(r.toTier)).name} now (tier ${r.toTier}).`, true);
        else if (r.state === 'defended') this.ladderResult('SEAT DEFENDED!', `${r.challenger.initials} stays where they are.`, true);
        else if (r.mine === 'challenger' && !r.emptySeat) this.banner(`CHALLENGE LAP ${fmtTime(r.challengerTicks)}`, 'Keep improving, or send it from the Ladder page', 'good', 2.2);
        else if (r.mine === 'challenger' && r.target !== null) this.banner(`${fmtTime(g.ticks)}`, `${((g.ticks - r.target) / 120).toFixed(2)} s off the benchmark`, 'bad', 2);
        else if (r.mine === 'defender' && r.target !== null) this.banner(`${fmtTime(g.ticks)}`, `${((g.ticks - r.target) / 120).toFixed(2)} s slower than ${r.challenger.initials}`, 'bad', 2);
      })
      .catch((e) => {
        this.banner('NOT SENT', e instanceof ApiError ? e.message : 'No connection: try again in a moment', 'bad', 2.5);
      });
  }

  private ladderResult(title: string, sub: string, good: boolean): void {
    this.trialCtx = null;
    this.mode = 'cooldown';
    this.cooldown = 1.2;
    this.audio.blip(good ? 1568 : 196, 0.4);
    this.afterCooldown = () =>
      this.sheet('SEAT LADDER', title, sub, '', [
        { label: 'LADDER', primary: true, onClick: () => this.go('/ladder') },
        { label: 'HOME', onClick: () => this.showTitle() },
      ]);
  }

  // ========================================================= Daily Grand Prix

  /** Practice (unlimited, kind 'trial') or the one ranked run (kind 'daily'). */
  private startDaily(spec: DailySpec, ranked: boolean): void {
    this.audio.unlock();
    this.hideAll();
    if (this.couch) this.couch.match = null;
    this.dailyCtx = { spec, ranked, inputs: [], done: false };
    this.kind = ranked ? 'daily' : 'trial';
    this.race = null;
    this.weekend = null;
    this.attract = null;
    this.renderer.setRivals([]);
    this.useTrack(trackById(spec.trackId));
    this.newWorld();
    this.renderer.setConditions(spec.conditions);
    this.setHudTeam(this.team);
    this.showHud();
    if (ranked) this.banner('RANKED RUN', 'One shot. Make it count.', '', 2.4);
    else this.banner(`DAILY #${spec.number} PRACTICE`, 'R restarts · as many laps as you like', '', 2.4);
    this.startLights();
  }

  private stepRanked(input: Input): void {
    const ctx = this.dailyCtx!;
    if (ctx.done) return;
    this.prev = snap(this.world.car);
    ctx.inputs.push(input);
    this.world.step(input);
    this.skidFor(this.world.car);
    // Same end rules, in the same order, as the server's replay (sim/daily.ts runToEnd).
    for (const e of this.world.events) {
      if (e.type === 'crash') {
        this.crashFx(this.world.car);
        this.finishRanked('dnf', null, 'CRASH!');
        return;
      }
      if (e.type === 'missedCheckpoint') {
        this.finishRanked('dnf', null, 'MISSED A CHECKPOINT');
        return;
      }
      if (e.type === 'checkpoint') {
        const ref = this.ghost?.splits?.[e.gate];
        if (ref !== undefined) {
          const d = this.world.currentLapTicks() - ref;
          this.banner(fmtDelta(d), 'vs your practice best', d <= 0 ? 'good' : 'bad', 1.1);
        }
      }
      if (e.type === 'lap') {
        if (e.valid) this.finishRanked('ok', e.ticks, fmtTime(e.ticks));
        else this.finishRanked('dnf', null, 'LAP NOT COUNTED');
        return;
      }
      if (e.type === 'streak') this.banner('+1 MPH  TUNED', `Clean flat-out driving: +${e.bonusMph} mph`, 'good', 1.6);
    }
    if (ctx.inputs.length >= DAILY_MAX_TICKS) this.finishRanked('dnf', null, 'TIME UP');
  }

  private finishRanked(status: 'ok' | 'dnf', ticks: number | null, msg: string): void {
    const ctx = this.dailyCtx;
    if (!ctx || ctx.done) return;
    ctx.done = true;
    this.banner(msg, status === 'ok' ? 'Handing in your lap\u2026' : 'DNF', status === 'ok' ? 'good' : 'bad', 2);
    this.audio.blip(status === 'ok' ? 1568 : 196, 0.3);
    this.mode = 'cooldown';
    this.cooldown = 1.6;
    this.afterCooldown = () => void this.submitRanked(status, ticks);
  }

  private async submitRanked(status: 'ok' | 'dnf', ticks: number | null): Promise<void> {
    const ctx = this.dailyCtx!;
    const spec = ctx.spec;
    const payload = { date: spec.date, inputs: encodeInputs(ctx.inputs), status, ticks };
    this.sheet(`DAILY #${spec.number} · RESULT`, 'HANDING IN\u2026', 'The server is replaying your lap.', '', []);
    try {
      const r = await api.submitDaily(payload.date, payload.inputs, payload.status, payload.ticks);
      dropQueuedDaily(spec.date);
      this.showRankedResult(spec, r.run, r.streak, r.leaderSplits);
    } catch (e) {
      if (e instanceof ApiError) {
        this.sheet(`DAILY #${spec.number} · RESULT`, 'NOT COUNTED', e.message, '', [
          { label: 'DAILY', primary: true, onClick: () => this.go('/daily') },
          { label: 'HOME', onClick: () => this.showTitle() },
        ]);
      } else {
        queueDaily(payload);
        this.sheet(`DAILY #${spec.number} · RESULT`, 'SAVED FOR LATER', "No connection. Your run is saved and goes in when you're back online (until 00:15 UK).", '', [
          { label: 'TRY AGAIN', primary: true, onClick: () => void this.submitRanked(status, ticks) },
          { label: 'HOME', onClick: () => this.showTitle() },
        ]);
      }
    }
  }

  private showRankedResult(spec: DailySpec, run: RunSummary, streak: Streak, leaderSplits: number[]): void {
    const text = shareText(spec, run, leaderSplits, streak);
    const body = document.createElement('div');
    body.innerHTML = `<div class="daily-result">${this.site.resultHtml(spec, run, { entries: run.total })}</div>
      ${streak.current > 0 ? `<div class="streak">\u{1F525} ${streak.current}-day streak${streak.current >= streak.best && streak.current > 1 ? ' (your best!)' : ''}</div>` : ''}
      <pre class="share-pre"></pre>`;
    (body.querySelector('.share-pre') as HTMLElement).textContent = text;
    this.sheet(`DAILY #${spec.number} · RESULT`, run.status === 'ok' ? `P${run.rank} OF ${run.total}` : 'DNF', `${trackById(spec.trackId).def.name} · ${this.team.name}`, body, [
      {
        label: 'SHARE',
        primary: true,
        onClick: () => {
          void share(text).then((r) => {
            const b = $('sheet-actions').querySelector('button.primary') as HTMLButtonElement | null;
            if (b) b.textContent = r === 'copied' ? 'COPIED!' : r === 'shared' ? 'SHARED' : 'SHARE';
          });
        },
      },
      { label: 'LEADERBOARD', onClick: () => this.go('/daily') },
      { label: 'HOME', onClick: () => this.showTitle() },
    ]);
  }

  /** A Time Trial best goes to the records board (the server replays it first). */
  private submitLap(g: GhostData): void {
    if (!loadProfile()) return;
    const lap = { track: this.track.def.id, team: g.team, ticks: g.ticks, start: g.start, inputs: encodeInputs(g.inputs) };
    api
      .submitLap(lap)
      .then((r) => {
        if (r.rank <= 3) this.banner(`RECORDS BOARD P${r.rank}`, 'Your lap is on the board', 'good', 2);
      })
      .catch((e) => {
        if (!(e instanceof ApiError)) queueLap(lap);
      });
  }

  private restartTrial(): void {
    if (this.kind !== 'trial') return;
    $('pause').hidden = true;
    this.world.restart();
    this.world.lap.bestLapTicks = loadBest(this.trialKey, this.team.id);
    this.prev = snap(this.world.car);
    this.rec = null;
    this.ghostWorld = null;
    this.renderer.clearSkids();
    this.acc = 0;
    this.startLights();
  }

  private stepTrial(input: Input): void {
    this.prev = snap(this.world.car);
    if (this.rec) this.rec.inputs.push(input);
    this.world.step(input);
    if (this.ghostWorld && this.ghost) {
      this.ghostPrev = snap(this.ghostWorld.car);
      if (this.ghostTick < this.ghost.inputs.length) this.ghostWorld.step(this.ghost.inputs[this.ghostTick++]);
      else this.ghostWorld = null;
    }
    this.skidFor(this.world.car);
    for (const e of this.world.events) this.onTrialEvent(e);
  }

  private onTrialEvent(e: World['events'][number]): void {
    const lap = this.world.lap;
    switch (e.type) {
      case 'lapStart': {
        const c = this.world.car;
        this.rec = {
          start: { x: c.x, y: c.y, vx: c.vx, vy: c.vy, h: c.h, bonusMph: c.bonusMph, streakDist: lap.streakDist },
          inputs: [],
          splits: [],
        };
        this.startGhost();
        break;
      }
      case 'checkpoint': {
        const t = this.world.currentLapTicks();
        if (this.rec) this.rec.splits[e.gate] = t;
        const ref = this.ghost?.splits?.[e.gate];
        if (ref !== undefined && lap.valid) {
          const d = t - ref;
          this.banner(fmtDelta(d), '', d <= 0 ? 'good' : 'bad', 1.1);
        }
        break;
      }
      case 'lap':
        if (e.valid && e.best) {
          saveBest(this.trialKey, this.team.id, e.ticks);
          if (this.rec) {
            this.ghost = { v: 1, track: this.trialKey, team: this.team.id, ticks: e.ticks, start: this.rec.start, splits: this.rec.splits, inputs: this.rec.inputs };
            saveGhost(this.ghost);
            this.renderer.setGhost(this.team);
            // A Time Trial best goes to the online records board (replayed there).
            if (!this.dailyCtx) this.submitLap(this.ghost);
            if (this.trialCtx?.kind === 'ladder') this.submitChallengeLap(this.ghost);
            // Racing someone's ghost: keep theirs on track, not yours.
            if (this.trialCtx?.ghost) {
              const theirs = this.trialCtx.ghost;
              if (this.trialCtx.kind === 'ghost' && e.ticks < theirs.ticks) this.banner(`YOU BEAT ${this.trialCtx.ghostName}!`, `by ${((theirs.ticks - e.ticks) / 120).toFixed(2)} s`, 'good', 3);
              this.ghost = theirs;
              this.renderer.setGhost(teamById(theirs.team));
            }
          }
          const rec = Math.round(this.track.def.defaultRecord * 120);
          this.banner(`NEW BEST ${fmtTime(e.ticks)}`, e.ticks < rec ? 'Under the lap record!' : 'Ghost saved: race yourself', 'good', 2.2);
          this.audio.blip(1568, 0.25);
        } else if (e.valid) {
          const best = lap.bestLapTicks;
          this.banner(`LAP ${fmtTime(e.ticks)}`, best ? fmtDelta(e.ticks - best) : '', 'bad', 1.8);
        } else {
          this.banner('LAP NOT COUNTED', 'You crashed on that lap', 'bad', 1.8);
        }
        break;
      case 'missedCheckpoint':
        this.banner('MISSED A CHECKPOINT', 'Lap not counted', 'bad', 1.8);
        break;
      case 'crash':
        this.crashFx(this.world.car);
        this.banner('CRASH!', 'That lap is gone', 'bad', 1.3);
        this.rec = null;
        this.ghostWorld = null;
        break;
      case 'respawn':
        this.prev = snap(this.world.car);
        break;
      case 'streak':
        this.banner('+1 MPH  TUNED', `Clean flat-out driving: +${e.bonusMph} mph`, 'good', 1.6);
        this.audio.blip(990, 0.12);
        break;
    }
  }

  private startGhost(): void {
    if (!this.ghost) return;
    const g = this.ghost;
    const team = teamById(g.team);
    const w = new World(this.track, team, 0, this.mods);
    Object.assign(w.car, { x: g.start.x, y: g.start.y, vx: g.start.vx, vy: g.start.vy, h: g.start.h, bonusMph: g.start.bonusMph });
    w.perf = carPerf(team, g.start.bonusMph);
    w.lap.streakDist = g.start.streakDist;
    this.ghostWorld = w;
    this.ghostPrev = snap(w.car);
    this.ghostTick = 0;
  }

  // ======================================================= weekends (shared)

  private aiPace(): number {
    return AI_PACE[this.weekend?.kind === 'season' && this.season ? this.season.difficulty : this.difficulty];
  }

  private raceCrashRule(): CrashRuleName {
    return this.weekend?.kind === 'season' && this.season ? this.season.crashRule : this.crashRule;
  }

  /** Quick race: your chosen team on your chosen track, one round. */
  private startQuickRace(): void {
    this.audio.unlock();
    const me = this.team;
    const specs: RacerSpec[] = [{ id: PLAYER_ID, name: 'YOU', tag: 'YOU', team: me }];
    for (const d of DRIVERS) {
      // The driver whose team you took gets your usual Bennet.
      const team = d.teamId === me.id ? teamById(DEFAULT_TEAM) : teamById(d.teamId);
      specs.push({ id: d.id, name: d.name, tag: d.tag, team, driver: d });
    }
    this.beginWeekend('quick', new Track(CALENDAR[this.trackIndex]), specs, (Math.random() * 0xffffffff) >>> 0);
    this.showQualIntro();
  }

  private beginWeekend(kind: Weekend['kind'], track: Track, specs: RacerSpec[], seed: number, challenges: Challenge[] = []): void {
    this.useTrack(track);
    const wk: Weekend = { kind, track, seed, specs, me: 0, qual: [], grid: [], results: null, challenges };
    this.weekend = wk;
    const pace = this.aiPace();
    // AI challengers are fired up all weekend (qualifying and race).
    const qs = raceSpecs(specs, challenges);
    wk.qual = qs.map((s, i) => (s.driver ? qualifyAi(track, s, pace, seed + i * 7919) : null));
  }

  private showQualIntro(): void {
    const wk = this.weekend!;
    const def = wk.track.def;
    const me = wk.specs[wk.me].team;
    const card = document.createElement('div');
    card.className = 'round-card';
    card.innerHTML = `<div>${trackMapSvg(wk.track, 150)}</div>
      <ol class="rules">
        <li>One flying lap. Your time sets your grid slot.</li>
        <li>Crash and you start from the back.</li>
        ${wk.track.carparks.length ? '<li>The car park is a legal shortcut, but the parked cars are solid.</li>' : ''}
        <li>Race: ${def.raceLaps} laps, points 10-6-4-3-2-1.</li>
      </ol>`;
    const round = wk.kind === 'season' ? `ROUND ${this.season!.round + 1}/6 · ` : 'QUICK RACE · ';
    this.sheet(`${round}${def.country} · QUALIFYING`, def.name, `${def.subtitle}. You drive the ${me.name}.`, card, [
      { label: 'QUALIFY', primary: true, onClick: () => this.startQualifying() },
      { label: 'MENU', onClick: () => this.showTitle() },
    ]);
  }

  private startQualifying(): void {
    const wk = this.weekend!;
    this.kind = 'qual';
    this.race = new RaceWorld(wk.track, [wk.specs[wk.me]], [0], { laps: 1, crashRule: 'classic', aiPace: this.aiPace(), seed: wk.seed });
    this.meIdx = 0;
    this.setupRaceRender();
    this.hideAll();
    this.showHud();
    this.setHudTeam(wk.specs[wk.me].team);
    this.startLights();
  }

  private finishQualifying(ticks: number | null): void {
    const wk = this.weekend!;
    wk.qual[wk.me] = ticks;
    wk.grid = gridFromQualifying(wk.qual);
    this.mode = 'cooldown';
    this.cooldown = 1.6;
    this.afterCooldown = () => this.showGrid();
  }

  private showGrid(): void {
    const wk = this.weekend!;
    const table = document.createElement('table');
    table.className = 'board';
    const pole = wk.qual[wk.grid[0]];
    table.innerHTML = `<tr><th>POS</th><th>DRIVER</th><th>TEAM</th><th class="num">TIME</th><th class="num">GAP</th></tr>`;
    wk.grid.forEach((idx, pos) => {
      const s = wk.specs[idx];
      const t = wk.qual[idx];
      const tr = document.createElement('tr');
      if (idx === wk.me) tr.className = 'me';
      tr.innerHTML = `<td>${pos + 1}</td><td><span class="chip" style="background:#${hex(s.team.primary)}"></span>${s.name}</td><td>${s.team.name}</td>
        <td class="num">${t === null ? 'NO TIME' : fmtTime(t)}</td><td class="num">${t === null || pole === null || pos === 0 ? '' : fmtDelta(t - pole!)}</td>`;
      table.appendChild(tr);
    });
    const myPos = wk.grid.indexOf(wk.me) + 1;
    const myTime = wk.qual[wk.me];
    const next = wk.kind === 'season'
      ? { label: 'TO THE PADDOCK', primary: true, onClick: () => this.showPaddock() }
      : { label: 'START RACE', primary: true, onClick: () => this.startRace() };
    this.sheet(
      `${wk.track.def.country} · GRID`,
      myTime === null ? 'BACK OF THE GRID' : myPos === 1 ? 'POLE POSITION!' : `YOU START P${myPos}`,
      myTime === null ? 'No qualifying time: you start last.' : `Your lap: ${fmtTime(myTime)}`,
      table,
      [next, { label: 'MENU', onClick: () => this.showTitle() }],
    );
  }

  private startRace(): void {
    const wk = this.weekend!;
    this.kind = 'race';
    const specs = wk.kind === 'season' ? raceSpecs(wk.specs, wk.challenges) : wk.specs;
    this.race = new RaceWorld(wk.track, specs, wk.grid, {
      laps: wk.track.def.raceLaps,
      crashRule: this.raceCrashRule(),
      aiPace: this.aiPace(),
      seed: wk.seed ^ 0x5eed,
    });
    this.meIdx = wk.me;
    this.lastPos = wk.grid.indexOf(wk.me) + 1;
    this.setupRaceRender();
    this.hideAll();
    this.showHud();
    this.setHudTeam(wk.specs[wk.me].team);
    this.startLights();
  }

  private setupRaceRender(): void {
    const race = this.race!;
    const me = race.racers[this.meIdx];
    this.renderer.setTeam(me.spec.team);
    this.renderer.setGhost(null);
    this.renderer.clearSkids();
    this.renderer.setRivals(race.racers.filter((r) => r.index !== this.meIdx).map((r) => ({ team: r.spec.team, tag: r.spec.tag })));
    this.prevCars = race.racers.map((r) => snap(r.car));
  }

  /** The challenge you're in this race, if any: index of the rival racer. */
  private rivalIdx(): number {
    const wk = this.weekend;
    if (!wk || wk.kind !== 'season' || this.kind !== 'race') return -1;
    const c = wk.challenges.find((x) => x.challenger === PLAYER_ID || x.defender === PLAYER_ID);
    if (!c) return -1;
    const other = c.challenger === PLAYER_ID ? c.defender : c.challenger;
    return wk.specs.findIndex((s) => s.id === other);
  }

  private stepRace(input: Input): void {
    const race = this.race!;
    this.prevCars = race.racers.map((r) => snap(r.car));
    const me = race.racers[this.meIdx];
    let myInput = input;
    if (this.autoDrive && me.status === 'running') {
      myInput = aiInput(me.ai, AUTO_DRIVER, {
        car: me.car, perf: me.perf, track: this.track, lapsDone: me.lap.lapsDone, basePace: 1.25,
        traffic: race.racers.map((r) => ({ car: r.car, solid: r.status === 'running' && !r.car.crashed })),
      });
    }
    race.step(this.mode === 'playing' ? new Map([[this.meIdx, myInput]]) : new Map());
    for (const r of race.racers) this.skidFor(r.car);
    for (const e of race.events) this.onRaceEvent(e);

    if (this.kind === 'race') {
      const pos = race.standings().indexOf(this.meIdx) + 1;
      if (pos !== this.lastPos && me.status === 'running') {
        const el = $('hud-pos');
        el.classList.remove('bump');
        if (pos < this.lastPos) {
          void el.offsetWidth;
          el.classList.add('bump');
        }
        this.lastPos = pos;
      }
    }
  }

  private onRaceEvent(e: RaceEvent): void {
    const race = this.race!;
    const mine = 'racer' in e && e.racer === this.meIdx;
    switch (e.type) {
      case 'crash': {
        const car = race.racers[e.racer].car;
        if (mine) {
          this.crashFx(car);
          if (this.kind === 'qual') this.banner('CRASH!', 'No time: back of the grid', 'bad', 2);
          else if (this.raceCrashRule() === 'classic') this.banner('CRASH!', 'Your race is over', 'bad', 2.5);
          else this.banner('CRASH!', 'Back on track in 3 seconds', 'bad', 2);
        } else {
          const me = race.racers[this.meIdx].car;
          const near = Math.hypot(car.x - me.x, car.y - me.y) < 400;
          this.flashes.push({ x: car.x, y: car.y, f: 1 });
          if (near) {
            this.audio.crash();
            this.banner(`${race.racers[e.racer].spec.tag} CRASHED!`, '', 'good', 1.2);
          }
        }
        break;
      }
      case 'out':
        if (mine) {
          if (this.kind === 'qual') this.finishQualifying(null);
          else this.playerDone();
        }
        break;
      case 'respawn':
        this.prevCars[e.racer] = snap(race.racers[e.racer].car);
        break;
      case 'lap':
        if (!mine) break;
        if (this.kind === 'qual') {
          this.banner(e.valid ? `QUALIFYING ${fmtTime(e.ticks)}` : 'LAP NOT COUNTED', '', e.valid ? 'good' : 'bad', 2);
        } else {
          const r = race.racers[this.meIdx];
          if (r.lap.lapsDone < race.opts.laps) {
            const last = r.lap.lapsDone + 1 === race.opts.laps;
            this.banner(last ? 'FINAL LAP' : `LAP ${r.lap.lapsDone + 1}/${race.opts.laps}`, fmtTime(e.ticks), e.best ? 'good' : '', 1.6);
          }
        }
        break;
      case 'finish':
        if (!mine) break;
        if (this.kind === 'qual') {
          this.finishQualifying(race.racers[this.meIdx].lap.lastLapTicks);
        } else {
          const p = race.standings().indexOf(this.meIdx) + 1;
          this.banner(p === 1 ? 'YOU WIN!' : `FINISHED P${p}`, '', p <= 3 ? 'good' : '', 3);
          this.audio.blip(p === 1 ? 1568 : 1046, 0.3);
          this.playerDone();
        }
        break;
      case 'missedCheckpoint':
        if (mine) this.banner('MISSED A CHECKPOINT', 'Lap not counted', 'bad', 1.8);
        break;
      case 'streak':
        if (mine) {
          this.banner('+1 MPH  TUNED', `Clean flat-out driving: +${e.bonusMph} mph`, 'good', 1.6);
          this.audio.blip(990, 0.12);
        }
        break;
      case 'contact':
        if (e.a === this.meIdx || e.b === this.meIdx) {
          this.audio.blip(110, 0.06);
          if (navigator.vibrate) navigator.vibrate(30);
        }
        break;
      case 'raceOver':
        if (this.kind === 'race' && this.mode !== 'cooldown') this.playerDone();
        break;
      default:
        break;
    }
  }

  private playerDone(): void {
    if (this.mode === 'cooldown') return;
    this.mode = 'cooldown';
    this.cooldown = 3;
    this.afterCooldown = () => {
      this.race!.simulateToEnd();
      this.showResults();
    };
  }

  private retire(): void {
    $('pause').hidden = true;
    if (this.kind === 'online') {
      this.online.leaveRace();
      return;
    }
    if (this.kind === 'daily') {
      this.finishRanked('dnf', null, 'RETIRED');
      return;
    }
    if (this.kind === 'couch') {
      this.couch.endMatch();
      return;
    }
    if (this.kind === 'trial') {
      if (this.dailyCtx) this.go('/daily');
      else if (this.trialCtx?.kind === 'ladder') this.go('/ladder');
      else this.showTitle();
      return;
    }
    if (this.kind === 'qual') {
      this.finishQualifying(null);
      this.cooldown = 0;
      return;
    }
    const race = this.race!;
    const me = race.racers[this.meIdx];
    if (me.status === 'running') {
      me.outProgress = race.progress(me);
      me.status = 'out';
    }
    this.mode = 'cooldown';
    this.cooldown = 0;
    this.afterCooldown = () => {
      race.simulateToEnd();
      this.showResults();
    };
  }

  private showResults(): void {
    const wk = this.weekend!;
    const race = this.race!;
    const res = race.results();
    wk.results = res;
    const fastest = res.reduce<ResultRow | null>((b, r) => (r.bestLapTicks !== null && (b === null || r.bestLapTicks < b.bestLapTicks!) ? r : b), null);
    const table = document.createElement('table');
    table.className = 'board';
    table.innerHTML = `<tr><th>POS</th><th>DRIVER</th><th>TEAM</th><th class="num">TIME</th><th class="num">BEST LAP</th><th class="num">PTS</th></tr>`;
    const winner = res[0].timeTicks;
    for (const r of res) {
      const s = race.racers[r.racer].spec;
      const tr = document.createElement('tr');
      if (r.racer === this.meIdx) tr.className = 'me';
      const time = r.status === 'out' ? 'OUT' : r.position === 1 ? fmtTime(r.timeTicks) : winner !== null && r.timeTicks !== null ? fmtDelta(r.timeTicks - winner) : '';
      tr.innerHTML = `<td>${r.position}</td><td><span class="chip" style="background:#${hex(s.team.primary)}"></span>${s.name}</td><td>${s.team.name}</td>
        <td class="num">${time}</td><td class="num ${fastest === r ? 'fl' : ''}">${fmtTime(r.bestLapTicks)}</td><td class="num">${r.points}</td>`;
      table.appendChild(tr);
    }
    const mine = res.find((r) => r.racer === this.meIdx)!;
    const stats = loadStats();
    stats.races++;
    stats.points += mine.points;
    if (mine.position === 1 && mine.status === 'finished') stats.wins++;
    if (mine.position <= 3 && mine.status === 'finished') stats.podiums++;
    saveStats(stats);
    const title =
      mine.status === 'out' ? 'DID NOT FINISH' : mine.position === 1 ? 'VICTORY!' : mine.position <= 3 ? `PODIUM: P${mine.position}` : `FINISHED P${mine.position}`;
    const sub = `${mine.points} point${mine.points === 1 ? '' : 's'}${fastest?.racer === this.meIdx ? ' · fastest lap!' : ''}`;

    if (wk.kind === 'season' && this.season) {
      // Bank the round: points, challenges, seat swaps. Saved immediately.
      const s = this.season;
      const outcome = applyRound(
        s,
        wk.track.def.id,
        res.map((r) => ({ driver: wk.specs[r.racer].id, status: r.status, timeTicks: r.timeTicks, bestLapTicks: r.bestLapTicks })),
        wk.challenges,
      );
      saveSeasonRaw(s);
      this.sheet(`ROUND ${s.round}/6 · ${wk.track.def.country} · RESULT`, title, sub, table, [
        outcome.challenges.length
          ? { label: 'CHALLENGES', primary: true, onClick: () => this.showChallengeResults(outcome.challenges) }
          : { label: 'CHAMPIONSHIP', primary: true, onClick: () => this.showStandings() },
      ]);
      return;
    }
    this.sheet('QUICK RACE · RESULT', title, sub, table, [
      { label: 'RACE AGAIN', primary: true, onClick: () => this.startQuickRace() },
      { label: 'MENU', onClick: () => this.showTitle() },
    ]);
  }

  // ================================================================= season

  private seasonButton(): void {
    this.audio.unlock();
    if (!this.season || seasonOver(this.season)) {
      this.season = newSeason((Math.random() * 0xffffffff) >>> 0, this.difficulty, this.crashRule);
      saveSeasonRaw(this.season);
    }
    this.showRoundCard();
  }

  private confirmNewSeason(): void {
    this.sheet('NEW SEASON', 'START OVER?', 'Your current season and its points will be lost.', '', [
      {
        label: 'YES, NEW SEASON',
        primary: true,
        onClick: () => {
          clearSeason();
          this.season = null;
          this.seasonButton();
        },
      },
      { label: 'KEEP MY SEASON', onClick: () => this.showTitle() },
    ]);
  }

  private showRoundCard(): void {
    const s = this.season!;
    const def = CALENDAR[s.round];
    const track = new Track(def);
    this.useTrack(track);
    const table = standings(s);
    const myPos = table.findIndex((r) => r.driver === PLAYER_ID) + 1;
    const leader = table[0];
    const card = document.createElement('div');
    card.className = 'round-card';
    card.innerHTML = `<div>${trackMapSvg(track, 150)}</div>
      <div class="facts">
        <div>${def.subtitle}</div>
        <div>${def.raceLaps} laps &middot; lap record <b>${fmtTime(Math.round(def.defaultRecord * 120))}</b></div>
        <div>Your car: <b>${teamById(s.seats[PLAYER_ID]).name}</b> (tier ${teamById(s.seats[PLAYER_ID]).tier})</div>
        <div>${s.round > 0 ? `Championship: <b>P${myPos}</b>, ${s.points[PLAYER_ID]} pts &middot; leader ${driverName(leader.driver)} ${leader.points}` : 'Season opener: everyone on 0 pts'}</div>
        <div class="muted">One flying lap sets the grid. Then the paddock: challenge a rival for their car.</div>
      </div>`;
    this.sheet(`ROUND ${s.round + 1} OF 6 · ${def.country}`, def.name, `AI ${s.difficulty.toUpperCase()} · crashes ${s.crashRule.toUpperCase()}`, card, [
      {
        label: 'QUALIFY',
        primary: true,
        onClick: () => {
          this.beginWeekend('season', track, seasonSpecs(s), (s.seed + s.round * 104729) >>> 0, aiChallenges(s));
          this.startQualifying();
        },
      },
      { label: 'STANDINGS', onClick: () => this.showStandings(true) },
      { label: 'MENU', onClick: () => this.showTitle() },
    ]);
  }

  /**
   * The paddock: the original's team screen. Browse the six teams; challenge
   * one driver for their car, or race as you are. AI challenges are shown
   * first, because if someone challenges you, you can't challenge anyone.
   */
  private showPaddock(): void {
    const s = this.season!;
    const wk = this.weekend!;
    const ai = wk.challenges.filter((c) => c.challenger !== PLAYER_ID);
    const engaged = engagedIn(ai);
    const incoming = ai.find((c) => c.defender === PLAYER_ID);
    const sheet = teamSheet(s);
    const table = standings(s);
    const wrap = document.createElement('div');

    const notice = document.createElement('div');
    if (incoming) {
      notice.className = 'notice bad';
      notice.innerHTML = `<b>${driverName(incoming.challenger)}</b> (${teamById(s.seats[incoming.challenger]).name}) challenges you for your <b>${teamById(s.seats[PLAYER_ID]).name}</b>! Finish ahead of ${driverName(incoming.challenger)} or lose your car. Challengers drive fired up: expect them to be quicker than usual.`;
    } else if (teamById(s.seats[PLAYER_ID]).tier === 1) {
      notice.className = 'notice info';
      notice.textContent = "You're in the fastest car on the grid. Nobody to challenge: just keep it.";
    } else {
      notice.className = 'notice info';
      notice.textContent = 'Pick a driver to challenge for their car: finish ahead of them and it\'s yours for the rest of the season. Lose, and nothing changes.';
    }
    wrap.appendChild(notice);
    const others = ai.filter((c) => c.defender !== PLAYER_ID);
    if (others.length) {
      const p = document.createElement('div');
      p.className = 'notice info';
      p.innerHTML = others.map((c) => `${driverName(c.challenger)} challenges ${driverName(c.defender)} for the ${teamById(s.seats[c.defender]).name}.`).join('<br>') + '<br><span class="muted">Challengers are fired up this weekend.</span>';
      wrap.appendChild(p);
    }

    const tbl = document.createElement('table');
    tbl.className = 'board';
    tbl.innerHTML = `<tr><th>TEAM</th><th>DRIVER</th><th class="num">0-100</th><th class="num">TOP</th><th class="num">PTS</th><th class="num">POS</th></tr>`;
    const rows: { tr: HTMLTableRowElement; driver: string }[] = [];
    for (const { team, driver } of sheet) {
      const t = teamById(team);
      const tr = document.createElement('tr');
      const pos = table.findIndex((r) => r.driver === driver) + 1;
      // You can only challenge for a better car (one that isn't already spoken for).
      const can = !incoming && driver !== PLAYER_ID && !engaged.has(driver) && t.tier < teamById(s.seats[PLAYER_ID]).tier;
      if (driver === PLAYER_ID) tr.className = 'me';
      else if (can) tr.className = 'pick';
      const why = driver !== PLAYER_ID && engaged.has(driver) ? ' <span class="muted">(busy)</span>' : '';
      tr.innerHTML = `<td><span class="chip" style="background:#${hex(t.primary)}"></span>${t.name}</td><td class="${driver === PLAYER_ID ? 'tag-you' : ''}">${driverName(driver)}${why}</td>
        <td class="num">${t.zeroTo100.toFixed(1)}s</td><td class="num">${t.topSpeedMph}</td><td class="num">${s.points[driver]}</td><td class="num">${s.round > 0 ? pos : '-'}</td>`;
      tbl.appendChild(tr);
      if (can) rows.push({ tr, driver });
    }
    wrap.appendChild(tbl);

    let sel = -1;
    const actionsFor = (): { label: string; primary?: boolean; onClick: () => void }[] => {
      const go = () => this.startRace();
      if (sel < 0) return [{ label: incoming ? 'ACCEPT & RACE' : 'RACE WITHOUT A CHALLENGE', primary: true, onClick: go }];
      const target = rows[sel].driver;
      return [
        {
          label: `CHALLENGE ${driverName(target)}`,
          primary: true,
          onClick: () => {
            wk.challenges = [...ai, { challenger: PLAYER_ID, defender: target }];
            go();
          },
        },
        { label: 'NO CHALLENGE', onClick: go },
      ];
    };
    const render = () => {
      rows.forEach((r, i) => r.tr.classList.toggle('sel', i === sel));
      const a = $('sheet-actions');
      a.innerHTML = '';
      for (const act of actionsFor()) {
        const btn = document.createElement('button');
        btn.textContent = act.label;
        btn.className = act.primary ? 'primary' : 'secondary';
        btn.onclick = act.onClick;
        a.appendChild(btn);
      }
    };
    rows.forEach((r, i) =>
      r.tr.addEventListener('click', () => {
        sel = sel === i ? -1 : i;
        render();
      }),
    );
    this.sheet(`ROUND ${s.round + 1}/6 · ${wk.track.def.country} · PADDOCK`, 'TEAM SCREEN', `You drive the ${teamById(s.seats[PLAYER_ID]).name}.`, wrap, []);
    render();
    if (!incoming && rows.length) {
      this.sheetNav = {
        up: () => {
          sel = sel <= -1 ? rows.length - 1 : sel - 1;
          render();
        },
        down: () => {
          sel = sel >= rows.length - 1 ? -1 : sel + 1;
          render();
        },
      };
    }
  }

  private showChallengeResults(outcomes: ReturnType<typeof applyRound>['challenges']): void {
    const s = this.season!;
    const ul = document.createElement('ul');
    ul.className = 'swap-list';
    // Yours first, like the original's CHALLENGE WINNER / LOSER screen.
    const sorted = [...outcomes].sort((a, b) => Number(b.challenger === PLAYER_ID || b.defender === PLAYER_ID) - Number(a.challenger === PLAYER_ID || a.defender === PLAYER_ID));
    for (const o of sorted) {
      const li = document.createElement('li');
      const mine = o.challenger === PLAYER_ID || o.defender === PLAYER_ID;
      if (mine) li.className = 'mine';
      const cT = teamById(o.challengerTeam).name;
      const dT = teamById(o.defenderTeam).name;
      // "YOU stay", "JOSH stays".
      const who = (id: string, verb: string) => `${driverName(id)} ${id === PLAYER_ID ? verb : verb + 's'}`;
      if (o.winner === null) {
        li.innerHTML = `${driverName(o.challenger)} vs ${driverName(o.defender)}: <b>both retired</b>. Nobody moves.`;
      } else if (o.swapped) {
        li.innerHTML = `<span class="w">CHALLENGE WINNER</span>: ${who(o.challenger, 'take')} the <b>${dT}</b>.<br><span class="l">CHALLENGE LOSER</span>: ${who(o.defender, 'drop')} to the <b>${cT}</b>.`;
      } else {
        li.innerHTML = `<span class="w">CHALLENGE WINNER</span>: ${who(o.defender, 'stay')} in the <b>${dT}</b>.<br><span class="l">CHALLENGE LOSER</span>: ${who(o.challenger, 'stay')} in the <b>${cT}</b>.`;
      }
      ul.appendChild(li);
    }
    const mineO = outcomes.find((o) => o.challenger === PLAYER_ID || o.defender === PLAYER_ID);
    let title = 'SEATS UNCHANGED';
    if (mineO) {
      if (mineO.swapped && mineO.challenger === PLAYER_ID) title = `YOU STOLE THE ${teamById(mineO.defenderTeam).name}!`;
      else if (mineO.swapped) title = `${driverName(mineO.challenger)} STOLE YOUR SEAT!`;
      else if (mineO.winner === PLAYER_ID) title = 'SEAT DEFENDED';
      else title = 'CHALLENGE LOST';
      this.audio.blip(mineO.winner === PLAYER_ID ? 1568 : 220, 0.3);
    } else if (outcomes.some((o) => o.swapped)) {
      title = 'SEATS CHANGE HANDS';
    }
    const myCar = teamById(s.seats[PLAYER_ID]).name;
    this.sheet(`ROUND ${s.round}/6 · CHALLENGES`, title, mineO?.swapped ? `You now drive the ${myCar}.` : `You keep the ${myCar}.`, ul, [
      { label: 'CHAMPIONSHIP', primary: true, onClick: () => this.showStandings() },
    ]);
  }

  private showStandings(fromRoundCard = false): void {
    const s = this.season!;
    const table = standings(s);
    const tbl = document.createElement('table');
    tbl.className = 'board';
    const last = s.rounds[s.rounds.length - 1];
    tbl.innerHTML = `<tr><th>POS</th><th>DRIVER</th><th>CAR</th><th class="num">WINS</th><th class="num">LAST</th><th class="num">PTS</th></tr>`;
    table.forEach((r, i) => {
      const t = teamById(s.seats[r.driver]);
      const lastPts = last?.entries.find((e) => e.driver === r.driver)?.points;
      const tr = document.createElement('tr');
      if (r.driver === PLAYER_ID) tr.className = 'me';
      tr.innerHTML = `<td>${i + 1}</td><td>${driverName(r.driver)}</td><td><span class="chip" style="background:#${hex(t.primary)}"></span>${t.name}</td>
        <td class="num">${r.wins}</td><td class="num">${lastPts === undefined ? '' : `+${lastPts}`}</td><td class="num">${r.points}</td>`;
      tbl.appendChild(tr);
    });
    if (fromRoundCard) {
      this.sheet('CHAMPIONSHIP', `AFTER ROUND ${s.round}`, '', tbl, [{ label: 'BACK', primary: true, onClick: () => this.showRoundCard() }]);
      return;
    }
    if (seasonOver(s)) {
      this.sheet(`AFTER ROUND ${s.round}/6`, 'FINAL STANDINGS', '', tbl, [{ label: 'SEASON REVIEW', primary: true, onClick: () => this.showFinale() }]);
      return;
    }
    const next = CALENDAR[s.round];
    this.sheet(`AFTER ROUND ${s.round}/6`, 'CHAMPIONSHIP', `Next: ${next.country} · ${next.name}`, tbl, [
      { label: 'NEXT ROUND', primary: true, onClick: () => this.showRoundCard() },
      { label: 'MENU', onClick: () => this.showTitle() },
    ]);
  }

  private showFinale(): void {
    const s = this.season!;
    const table = standings(s);
    const champ = table[0];
    const myPos = table.findIndex((r) => r.driver === PLAYER_ID) + 1;
    const youWon = champ.driver === PLAYER_ID;
    const steals = s.rounds.flatMap((r) => r.challenges).filter((c) => c.challenger === PLAYER_ID && c.swapped).length;
    const lost = s.rounds.flatMap((r) => r.challenges).filter((c) => c.defender === PLAYER_ID && c.swapped).length;
    const body = `<div class="trophy">${youWon ? '&#127942;' : '&#127937;'}</div>
      <div class="notice ${youWon ? 'good' : 'info'}">
        Champion: <b>${driverName(champ.driver)}</b> with ${champ.points} points.<br>
        You: <b>P${myPos}</b>, ${s.points[PLAYER_ID]} points, finishing the season in the <b>${teamById(s.seats[PLAYER_ID]).name}</b>.<br>
        Seats stolen: ${steals} &middot; seats lost: ${lost}
      </div>`;
    this.sheet('SEASON COMPLETE', youWon ? 'WORLD CHAMPION!' : `YOU FINISHED P${myPos}`, `AI ${s.difficulty.toUpperCase()} · crashes ${s.crashRule.toUpperCase()}`, body, [
      {
        label: 'NEW SEASON',
        primary: true,
        onClick: () => {
          this.season = null;
          clearSeason();
          this.seasonButton();
        },
      },
      { label: 'MENU', onClick: () => this.showTitle() },
    ]);
    if (youWon) this.audio.blip(1568, 0.5);
  }

  // ============================================================ shared loop

  private startLights(): void {
    document.body.classList.add('racing');
    this.mode = 'lights';
    this.lightsStart = performance.now();
    this.lastBeep = -1;
    $('lights').hidden = false;
    for (const l of document.querySelectorAll('.light')) l.classList.remove('on');
  }

  private pause(): void {
    if (this.kind === 'online' && this.online.racing) {
      // An online race can't stop for one player: offer to leave, keep racing.
      $('pause').hidden = !$('pause').hidden;
      $('p-restart').hidden = true;
      $('p-retire').hidden = false;
      $('p-retire').textContent = 'LEAVE RACE';
      return;
    }
    if (this.mode !== 'playing' && this.mode !== 'lights') return;
    this.mode = 'paused';
    $('pause').hidden = false;
    $('p-restart').hidden = this.kind !== 'trial';
    $('p-retire').hidden = this.kind === 'trial';
    $('p-retire').textContent = this.kind === 'qual' ? 'ABANDON LAP' : this.kind === 'couch' ? 'END MATCH' : this.kind === 'daily' ? 'RETIRE (DNF)' : 'RETIRE';
    this.audio.update(0, 0, false);
  }

  private resume(): void {
    $('pause').hidden = true;
    if (this.kind === 'online') return;
    this.acc = 0;
    this.startLights();
  }

  private frame(ts: number): void {
    const dtFrame = this.lastTs ? Math.min(0.1, (ts - this.lastTs) / 1000) : 1 / 60;
    this.lastTs = ts;
    const controls = this.input.read();
    this.lastControls = controls;

    if (this.mode === 'lights') this.updateLights(ts);

    if (this.kind === 'online' && this.online.racing) {
      // Online races run on the server's clock, paused or not.
      this.online.frame(dtFrame);
      this.updateHud(dtFrame);
      requestAnimationFrame((t) => this.frame(t));
      return;
    }
    if (this.kind === 'couch' && this.mode === 'playing') this.couch.readInputs();
    if (this.mode === 'playing' || this.mode === 'cooldown') {
      this.acc += dtFrame;
      let steps = 0;
      while (this.acc >= DT && steps < 8) {
        this.stepOnce(quantise(controls.steer, controls.throttle, controls.brake));
        this.acc -= DT;
        steps++;
      }
      if (steps === 8) this.acc = 0;
    }
    if (this.mode === 'cooldown') {
      this.cooldown -= dtFrame;
      if (this.cooldown <= 0 && this.afterCooldown) {
        const f = this.afterCooldown;
        this.afterCooldown = null;
        f();
      }
    }

    this.render(dtFrame);
    this.updateHud(dtFrame);
    requestAnimationFrame((t) => this.frame(t));
  }

  private stepOnce(input: Input): void {
    if (this.kind === 'couch') this.couch.step();
    else if (this.kind === 'daily') this.stepRanked(input);
    else if (this.kind === 'trial') this.stepTrial(input);
    else this.stepRace(input);
  }

  private render(dtFrame: number): void {
    if ((this.mode === 'title' || this.mode === 'site') && this.attract) {
      this.renderAttract(dtFrame);
      return;
    }
    const moving = this.mode === 'playing' || this.mode === 'cooldown';
    const alpha = moving ? this.acc / DT : 1;
    if (this.kind === 'couch' && this.couch.match && this.mode !== 'title') {
      this.couch.render(dtFrame, alpha);
      return;
    }
    for (const f of this.flashes) f.f -= dtFrame * 1.6;
    this.flashes = this.flashes.filter((f) => f.f > 0);

    let me: CarView;
    let myCar: CarState;
    let vmax: number;
    const rivals: (CarView & { alpha: number })[] = [];
    let ghost: CarView | null = null;
    if (this.kind !== 'trial' && this.race && this.mode !== 'title') {
      const race = this.race;
      const r = race.racers[this.meIdx];
      myCar = r.car;
      vmax = r.perf.vmax;
      me = lerpCar(this.prevCars[this.meIdx] ?? r.car, r.car, alpha);
      for (const o of race.racers) {
        if (o.index === this.meIdx) continue;
        const v = lerpCar(this.prevCars[o.index] ?? o.car, o.car, alpha);
        let a = 1;
        if (o.status === 'out') a = o.car.crashed ? Math.max(0, 1 - o.car.crashTicks / 160) : 0;
        else if (o.ghostTicks > 0 || o.status === 'finished') a = 0.5;
        rivals.push({ ...v, alpha: a });
      }
    } else {
      myCar = this.world.car;
      vmax = this.world.perf.vmax;
      me = lerpCar(this.prev, this.world.car, alpha);
      ghost = this.ghostWorld && this.ghostPrev ? lerpCar(this.ghostPrev, this.ghostWorld.car, alpha) : null;
    }
    this.renderer.draw(me, ghost, dtFrame, this.flashes, rivals);
    const ratio = Math.min(1, speedOf(myCar) / vmax);
    const live = this.mode === 'playing' || this.mode === 'lights' || this.mode === 'cooldown';
    this.audio.update(ratio, this.mode === 'playing' ? this.lastControls.throttle : 0.3, live);
  }

  private updateLights(ts: number): void {
    const t = (ts - this.lightsStart) / 1000;
    const lit = Math.min(5, Math.floor(t / 0.4));
    document.querySelectorAll('.light').forEach((l, i) => l.classList.toggle('on', i < lit));
    if (t >= 2.6) {
      $('lights').hidden = true;
      this.mode = 'playing';
      this.acc = 0;
      this.banner('GO!', '', 'good', 0.6);
      this.audio.blip(1320, 0.15);
    }
    const beepIdx = Math.floor(t / 0.4);
    if (beepIdx !== this.lastBeep && beepIdx >= 1 && beepIdx <= 5) this.audio.blip(440, 0.08);
    this.lastBeep = beepIdx;
  }

  private skidFor(car: CarState): void {
    const sp = speedOf(car);
    if (sp < 60 || car.crashed) return;
    const fx = Math.cos(car.h);
    const fy = Math.sin(car.h);
    const lat = Math.abs(-fy * car.vx + fx * car.vy);
    const off = car.surface === 'dust' || car.surface === 'grass' || car.surface === 'gravel';
    if (lat > 22 || off) this.renderer.addSkid(lerpCar(car, car, 1), Math.min(1, lat / 80));
  }

  private crashFx(car: CarState): void {
    this.flashes.push({ x: car.x, y: car.y, f: 1 });
    this.audio.crash();
    if (navigator.vibrate) navigator.vibrate(120);
  }

  // ===================================================================== HUD

  private setText(id: string, v: string): void {
    if (this.hudCache.get(id) === v) return;
    this.hudCache.set(id, v);
    $(id).textContent = v;
  }

  private updateHud(dtFrame: number): void {
    if (this.bannerTimer > 0) {
      this.bannerTimer -= dtFrame;
      if (this.bannerTimer <= 0) $('banner').classList.remove('show');
    }
    if (this.mode === 'title' || this.mode === 'sheet' || this.mode === 'site' || this.kind === 'online') return;
    if (this.kind === 'couch') {
      this.couch.hud();
      return;
    }

    let car: CarState;
    let lapTicks: number;
    let best: number | null;
    let streak: number;
    let laps: string;
    if (this.kind === 'trial' || this.kind === 'daily') {
      const w = this.world;
      car = w.car;
      lapTicks = w.currentLapTicks();
      best = w.lap.bestLapTicks;
      streak = w.lap.streakDist;
      laps = w.lap.startTick < 0 ? 'OUT LAP' : `LAP ${w.lap.lapsDone + 1}`;
      if (this.dailyCtx) laps = this.dailyCtx.ranked ? 'RANKED LAP' : `PRACTICE${w.lap.startTick < 0 ? '' : ` ${w.lap.lapsDone + 1}`}`;
    } else {
      const race = this.race!;
      const r = race.racers[this.meIdx];
      car = r.car;
      lapTicks = r.lap.startTick < 0 || r.status !== 'running' ? (r.lap.lastLapTicks ?? 0) : race.tick - r.lap.startTick;
      best = r.lap.bestLapTicks;
      streak = r.lap.streakDist;
      if (this.kind === 'qual') laps = r.lap.startTick < 0 ? 'QUALIFYING' : 'TIMED LAP';
      else if (r.status === 'finished') laps = 'FINISHED';
      else if (r.status === 'out') laps = 'OUT';
      else laps = `LAP ${Math.min(r.lap.lapsDone + 1, race.opts.laps)}/${race.opts.laps}`;
      if (this.kind === 'race') {
        const order = race.standings();
        const pos = order.indexOf(this.meIdx) + 1;
        const posEl = $('hud-pos');
        const key = `P${pos}/${order.length}`;
        if (this.hudCache.get('pos') !== key) {
          this.hudCache.set('pos', key);
          posEl.innerHTML = `P${pos}<small>/${order.length}</small>`;
        }
        const rival = this.rivalIdx();
        this.updateTower(order, rival);
        // Challenge tracker (design.md B10): you vs your rival, live.
        const rv = $('hud-rival');
        if (rival >= 0) {
          const rp = order.indexOf(rival) + 1;
          const ahead = pos < rp;
          const txt = `${ahead ? 'HOLD OFF' : 'CATCH'} ${race.racers[rival].spec.tag} (P${rp})`;
          if (this.hudCache.get('rival') !== txt) {
            this.hudCache.set('rival', txt);
            rv.hidden = false;
            rv.textContent = txt;
            rv.className = `rival ${ahead ? 'ahead' : 'behind'}`;
          }
        }
      }
    }
    this.setText('hud-time', fmtTime(lapTicks));
    this.setText('hud-best', fmtTime(best));
    this.setText('hud-laps', laps);
    this.setText('hud-speed', String(Math.round(mphFromPxs(speedOf(car)))));
    this.updatePips(car.bonusMph, streak);
  }

  private updateTower(order: number[], rival: number): void {
    const race = this.race!;
    const key = order.map((i) => `${i}${race.racers[i].status[0]}`).join(',') + `|${rival}`;
    if (this.hudCache.get('tower') === key) return;
    this.hudCache.set('tower', key);
    const ol = $('tower');
    ol.innerHTML = '';
    order.forEach((i, p) => {
      const r = race.racers[i];
      const li = document.createElement('li');
      if (i === this.meIdx) li.classList.add('me');
      if (i === rival) li.classList.add('rival');
      if (r.status === 'out') li.classList.add('out');
      const st = r.status === 'finished' ? '&#127937;' : r.status === 'out' ? 'OUT' : i === rival ? 'VS' : '';
      li.innerHTML = `<span>${p + 1}</span><span class="chip" style="background:#${hex(r.spec.team.primary)}"></span><span>${r.spec.tag}</span><span class="st">${st}</span>`;
      ol.appendChild(li);
    });
  }

  private updatePips(bonus: number, streakDist: number): void {
    const pips = $('hud-streak');
    const p = Math.min(100, (streakDist / (STREAK_LAP_FRACTION * this.track.length)) * 100);
    const key = `${bonus}:${Math.round(p / 5)}`;
    if (this.hudCache.get('pips') === key) return;
    this.hudCache.set('pips', key);
    pips.innerHTML = '';
    for (let i = 0; i < 5; i++) {
      const el = document.createElement('i');
      if (i < bonus) el.className = 'on';
      else if (i === bonus) {
        el.className = 'fill';
        el.style.setProperty('--p', `${p}%`);
      }
      pips.appendChild(el);
    }
  }

  private banner(text: string, sub: string, kind: 'good' | 'bad' | '' = '', secs = 1.5): void {
    const b = $('banner');
    b.className = `show ${kind}`;
    b.innerHTML = '';
    b.append(text);
    if (sub) {
      const s = document.createElement('span');
      s.className = 'sub';
      s.textContent = sub;
      b.append(s);
    }
    this.bannerTimer = secs;
  }

  // =================================================================== debug

  /** Test hook (only exposed with ?debug): the AI drives your car. */
  debugAuto(on = true): void {
    this.autoDrive = on;
    this.couch.autoDrive = on;
    this.online.autoDrive = on;
  }

  /** Test hook: run N sim ticks immediately (skips the lights). */
  debugAdvance(ticks: number): void {
    if (this.mode === 'lights') {
      $('lights').hidden = true;
      this.mode = 'playing';
    }
    for (let i = 0; i < ticks && (this.mode === 'playing' || this.mode === 'cooldown'); i++) {
      const input =
        this.kind === 'trial' || this.kind === 'daily' ? autopilot(this.world.car, this.world.perf, this.track, { pace: this.kind === 'daily' ? 0.95 : 1.3, lane: 0 }) : quantise(0, 0, 0);
      this.stepOnce(input);
    }
    this.acc = 0;
  }

  get debugState(): { mode: Mode; kind: Kind; race: RaceWorld | null; world: World; weekend: Weekend | null; season: SeasonState | null; couch: CouchMode; online: OnlineMode; shownTrack: string } {
    return { mode: this.mode, kind: this.kind, race: this.race, world: this.world, weekend: this.weekend, season: this.season, couch: this.couch, online: this.online, shownTrack: this.shownTrackId };
  }
}

function tierTeamName(tier: number): string {
  return [...TEAMS].sort((a, b) => a.tier - b.tier)[Math.max(1, Math.min(6, tier)) - 1].id;
}

function hex(n: number): string {
  return n.toString(16).padStart(6, '0');
}

function luminance(n: number): number {
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}


